
import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './SpeedChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ISpeedChartProps {
  values: number[];
  timeMarks: number[];
  average?: number;
  title?: string;
  subtitle?: string;
  bottomTextA?: string;
  bottomTextB?: string;
  sessionLabel?: string;
}

export const SpeedChart = ({
  values,
  timeMarks,
  average,
  title = "Speed Chart",
  subtitle = "Session statistics",
  bottomTextA = "Today's average speed",
  bottomTextB = "words per minute",
  sessionLabel = "Session"
}: ISpeedChartProps) => {
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const isSessionNumbers = timeMarks.every(mark => Number.isInteger(mark) && mark >= 1 && mark <= timeMarks.length);
  
  const labels = isSessionNumbers 
    ? timeMarks.map(sessionNum => `${sessionLabel} ${sessionNum}`)
    : timeMarks.map(time => formatTime(time));
  const currentSessionAverage = values.length > 0 ? values.reduce((acc, val) => acc + val, 0) / values.length : 0;

  // Calculate dynamic point radius based on number of data points
  const calculatePointRadius = (dataCount: number) => {
    if (dataCount <= 5) return 4; // Default size for small datasets
    if (dataCount <= 10) return 3;
    if (dataCount <= 20) return 2;
    return 1; // Minimum size for large datasets
  };

  const calculateHoverRadius = (dataCount: number) => {
    const baseRadius = calculatePointRadius(dataCount);
    return Math.max(baseRadius + 2, 3); // Ensure hover is at least 2px larger and minimum 3px
  };

  const pointRadius = calculatePointRadius(values.length);
  const pointHoverRadius = calculateHoverRadius(values.length);

  const data = {
    labels,
    datasets: [
      {
        label: 'Speed (w/m)',
        data: values,
        borderColor: '#FFA825',
        backgroundColor: 'rgba(255, 168, 37, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#FFA825',
        pointBorderColor: '#FFA825',
        pointRadius: pointRadius,
        pointHoverRadius: pointHoverRadius,
      },
      ...(average !== undefined ? [{
        label: 'Average',
        data: new Array(values.length).fill(average),
        borderColor: '#CAC4D0',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        tooltip: {
          display: false
        }
      }] : [])
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'white',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#E4E4E9',
        borderWidth: 1,
        displayColors: false,
        filter: function(tooltipItem: any) {
          return tooltipItem.datasetIndex === 0; // Only show first dataset (main speed data)
        },
        callbacks: {
          label: function(context: any) {
            return `${Math.floor(context.parsed.y)} w/m`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: '#E4E4E9',
          drawBorder: false,
        },
        ticks: {
          color: '#000',
          font: {
            size: 10,
          }
        }
      },
      y: {
        grid: {
          color: '#E4E4E9',
          drawBorder: false,
        },
        ticks: {
          color: '#000',
          font: {
            size: 10,
          },
          callback: function(value: any) {
            return Math.round(value);
          }
        }
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    elements: {
      point: {
        radius: pointRadius,
        hoverRadius: pointHoverRadius,
      }
    },
  };

  return (
    <div className="speed-chart">
      <div className="speed-chart__header">
        <div className="speed-chart__title">
          {title}
        </div>
        <div className="speed-chart__subtitle">
          {subtitle}
        </div>
        <div className="speed-chart__average">
          <span className="speed-chart__average-label">{bottomTextA}:</span>
          <span className="speed-chart__average-value">{Math.floor(currentSessionAverage)} {bottomTextB}</span>
        </div>
      </div>
      <div className="speed-chart__graph">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};
